import React from 'react'
import { hashHistory } from 'react-router'
import { Box, Tabs, Tab } from '@mui/material'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import ServerPagedTable from '../../../shared/ServerPagedTable'
import { groupLabel } from '../../lib/labels'

const TAB_STATUSES = [
  'pending_approval',
  'processing',
  'completed',
  'partially_failed',
  'rejected'
]
const TAB_LABELS = [
  'Pending Approval',
  'Processing',
  'Completed',
  'Partially Failed',
  'Rejected'
]

const headerCard = {
  background: '#FFFFFF',
  borderRadius: '8px',
  padding: '18px 20px'
}

const titleStyle = {
  fontFamily: 'Lato',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.0015em',
  color: '#000000',
  margin: 0
}

const tabsSx = {
  '& .MuiTabs-indicator': {
    backgroundColor: '#032A37'
  },
  '& .MuiTab-root': {
    fontFamily: 'Open Sans',
    textTransform: 'capitalize',
    fontSize: '13px',
    color: '#000000'
  },
  '& .MuiTab-root.Mui-selected': {
    color: '#fff',
    background: '#032A37',
    borderRadius: '8px 8px 0px 0px'
  }
}

export function formatKes(cents) {
  if (cents === null || cents === undefined || cents === '') {
    return '—'
  }
  const amount = Number(cents) / 100
  if (Number.isNaN(amount)) {
    return '—'
  }
  return `KES ${amount.toLocaleString()}`
}

export function titleCase(value) {
  if (!value || typeof value !== 'string') {
    return value
  }
  return value.replace(/_/g, ' ').replace(/\b\w/g, function(c) {
    return c.toUpperCase()
  })
}

export function StatusPill(props) {
  const raw = props.status ? String(props.status).toLowerCase() : ''
  const positive = ['completed', 'processing'].indexOf(raw) !== -1
  const negative = ['rejected', 'partially_failed'].indexOf(raw) !== -1
  let bg = '#DAE3E7'
  let color = '#8D8D8C'
  if (positive) {
    bg = '#CCFFDD'
    color = '#0A7D3A'
  } else if (negative) {
    bg = '#FDE2E2'
    color = '#8F191C'
  }
  return (
    <span
      style={{
        background: bg,
        color,
        borderRadius: '10px',
        padding: '3px 12px',
        fontSize: '11px',
        fontFamily: 'Open Sans',
        fontWeight: 600,
        display: 'inline-block',
        whiteSpace: 'nowrap'
      }}
    >
      {props.status ? titleCase(String(props.status)) : 'Unknown'}
    </span>
  )
}

class PaymentsList extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      tabIndex: 0
    }
    this.handleTabChange = this.handleTabChange.bind(this)
    this.fetchPage = this.fetchPage.bind(this)
  }

  // Only the tab moves here; the table owns the page and the page size and
  // asks for the first page of the newly selected status itself.
  handleTabChange(event, newIndex) {
    this.setState({ tabIndex: newIndex })
  }

  fetchPage(page, limit) {
    this.props.getPayments({
      status: TAB_STATUSES[this.state.tabIndex],
      page,
      limit
    })
  }

  render() {
    const payments = this.props.payments
    const data = payments && payments.data ? payments.data : []
    const total = payments ? payments.total : null
    // The page the rows on screen came from, echoed back by the endpoint, so the
    // footer cannot describe a page that was requested but never arrived.
    const page = payments ? payments.page : null
    const groupLabels = this.props.groupLabels

    const columns = [
      { title: 'Payment Reference', field: 'payment_reference' },
      {
        title: 'Group',
        field: 'group_guid',
        render(rowData) {
          return groupLabel(groupLabels, rowData.group_guid)
        }
      },
      {
        title: 'Amount',
        field: 'payment_amount_in_cents',
        render(rowData) {
          return formatKes(rowData.payment_amount_in_cents)
        }
      },
      {
        title: 'Total Premium',
        field: 'total_premium_amount_in_cents',
        render(rowData) {
          return formatKes(rowData.total_premium_amount_in_cents)
        }
      },
      {
        title: 'Status',
        field: 'status',
        render(rowData) {
          return <StatusPill status={rowData.status} />
        }
      },
      {
        title: 'Created At',
        field: 'created_at',
        render(rowData) {
          return rowData.created_at
            ? new Date(rowData.created_at).toLocaleString()
            : '—'
        }
      },
      {
        title: 'Action',
        sorting: false,
        render(rowData) {
          return (
            <ButtonInc
              variant="contained"
              onClick={function() {
                hashHistory.push(
                  `admin/group-payment-detail/${rowData.group_guid}/${rowData.guid}`
                )
              }}
            >
              View Payment
            </ButtonInc>
          )
        }
      }
    ]

    return (
      <div>
        <section style={headerCard}>
          <h1 style={titleStyle}>Group Payment Dashboard</h1>
        </section>

        <Box
          sx={{ borderBottom: 1, borderColor: 'divider', marginTop: '16px' }}
        >
          <Tabs
            value={this.state.tabIndex}
            onChange={this.handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="group payment dashboard"
            sx={tabsSx}
          >
            {TAB_LABELS.map(function(label, i) {
              return <Tab key={i} label={label} />
            })}
          </Tabs>
        </Box>

        <ServerPagedTable
          // The tab itself, not the status it maps to, so the reset does not
          // depend on every tab selecting a distinct status.
          resetKey={this.state.tabIndex}
          columns={columns}
          data={data}
          total={total}
          page={page}
          isLoading={this.props.loader}
          onFetch={this.fetchPage}
          options={{
            actionsColumnIndex: -1
          }}
          localization={{
            body: {
              emptyDataSourceMessage: 'No group payments found'
            }
          }}
        />
      </div>
    )
  }
}

export default PaymentsList
